import React, { useEffect, useState } from 'react';
import { api, AED, AED_short } from '../lib/api';
import { useAuth } from '../lib/auth';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { TrendingUp, Wallet, Building2, ClipboardCheck, Sparkles, ArrowRight, Loader2, Hammer } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import InvoiceModal from '../components/InvoiceModal';

const STAGE_COLORS = { sourcing: '#94A3B8', under_offer: '#F59E0B', owned: '#3B82F6', refurb: '#C2A878', listed: '#8B5CF6', sold: '#10B981' };
const STAGE_LABEL = { sourcing: 'Sourcing', under_offer: 'Under Offer', owned: 'Owned', refurb: 'Refurb', listed: 'Listed', sold: 'Sold' };

function Stat({ label, value, icon: Icon, hint, tone = 'default', testid }) {
  const toneMap = {
    default: 'text-slate-900',
    success: 'text-emerald-700',
    warn: 'text-amber-700',
    gold: 'text-[#8A6B3D]',
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="glass p-6 lift" data-testid={testid}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="overline">{label}</div>
        <div className="w-9 h-9 rounded-xl bg-white/70 grid place-items-center border border-white/60">
          <Icon size={16} className="text-slate-600" />
        </div>
      </div>
      <div className={`font-serif text-3xl ${toneMap[tone]}`}>{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-2">{hint}</div>}
    </motion.div>
  );
}

function CapitalSplitCard({ assets, build, committed, testid }) {
  const total = (assets || 0) + (build || 0);
  const assetsPct = total > 0 ? (assets / total) * 100 : 50;
  const buildPct  = total > 0 ? (build  / total) * 100 : 50;
  const uncommitted = Math.max(0, (committed || 0) - (build || 0));
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="glass p-6 lift" data-testid={testid}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="overline">Capital deployed</div>
        <div className="w-9 h-9 rounded-xl bg-white/70 grid place-items-center border border-white/60">
          <Wallet size={16} className="text-slate-600" />
        </div>
      </div>
      <div className="font-serif text-3xl text-slate-900 mb-3">{AED_short(total)}</div>

      {/* Stacked bar */}
      <div className="w-full h-2 rounded-full bg-slate-200/70 overflow-hidden flex mb-3" title={`${Math.round(assetsPct)}% assets / ${Math.round(buildPct)}% build`}>
        <div className="h-full bg-[#679286]" style={{ width: `${assetsPct}%` }} />
        <div className="h-full bg-[#C2A878]" style={{ width: `${buildPct}%` }} />
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs" data-testid="capital-split-breakdown">
        <div>
          <div className="flex items-center gap-1.5 text-slate-500">
            <span className="w-2 h-2 rounded-full bg-[#679286]"/>
            <Building2 size={11}/> In assets
          </div>
          <div className="font-medium text-slate-800 mt-0.5" data-testid="capital-assets-value">{AED_short(assets)}</div>
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-slate-500">
            <span className="w-2 h-2 rounded-full bg-[#C2A878]"/>
            <Hammer size={11}/> In build
          </div>
          <div className="font-medium text-slate-800 mt-0.5" data-testid="capital-build-value">{AED_short(build)}</div>
        </div>
      </div>

      {uncommitted > 0 && (
        <div className="text-[11px] text-slate-500 mt-3 pt-3 border-t border-slate-100">
          +{AED_short(uncommitted)} refurb committed but not yet spent
        </div>
      )}
    </motion.div>
  );
}

export default function Dashboard() {
  const { user, workspace } = useAuth();
  const [data, setData] = useState(null);
  const [briefing, setBriefing] = useState('');
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [viewInv, setViewInv] = useState(null);

  useEffect(() => {
    api.get('/dashboard/summary').then((r) => setData(r.data)).catch(() => setData({ kpis: {}, by_stage: {}, recent_invoices: [] }));
  }, []);

  const genBriefing = async () => {
    setBriefingLoading(true);
    try {
      const r = await api.get('/ai/briefing');
      setBriefing(r.data.briefing);
    } finally { setBriefingLoading(false); }
  };

  if (!data) return <div className="p-10 text-slate-500">Loading…</div>;
  const k = data.kpis || {};
  const stageData = Object.entries(data.by_stage || {}).map(([k, v]) => ({ name: STAGE_LABEL[k], value: v, color: STAGE_COLORS[k] }));

  return (
    <div className="max-w-[1400px] mx-auto py-6 space-y-8" data-testid="dashboard-page">
      {/* Greeting */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">{workspace === 'demo' ? 'Demo workspace' : 'Live workspace'}</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight">
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-600 mt-2">Here's what's moving across your Dubai portfolio.</p>
        </div>
        {workspace === 'demo' && (
          <Link to="/app/demo-tour" data-testid="start-demo-tour"
                className="btn-gold flex items-center gap-2 text-sm">
            <Sparkles size={14}/> Start guided tour
          </Link>
        )}
      </div>

      {/* KPI Bento */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <CapitalSplitCard testid="kpi-capital"
              assets={k.capital_in_assets_aed}
              build={k.capital_in_build_aed}
              committed={k.capital_build_committed_aed} />
        <Stat testid="kpi-active" label="Active projects" value={k.active_projects || 0} icon={Building2}
              hint={`${k.open_tasks || 0} open tasks`} />
        <Stat testid="kpi-pending" label="Pending approvals" value={AED_short(k.pending_approval_amount_aed)} icon={ClipboardCheck}
              hint={`${k.pending_approvals || 0} invoices waiting`} tone="warn" />
        <Stat testid="kpi-profit" label="Projected profit" value={AED_short(k.projected_profit_aed)} icon={TrendingUp}
              hint={`Realized ${AED_short(k.realized_profit_aed)}`} tone="gold" />
      </div>

      {/* Chart full width */}
      <div className="glass p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline">Pipeline</div>
            <div className="font-serif text-2xl">Properties by stage</div>
          </div>
          <Link to="/app/properties" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1">View all <ArrowRight size={12}/></Link>
        </div>
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart data={stageData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748B' }} />
              <YAxis tick={{ fontSize: 12, fill: '#64748B' }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 16, border: 'none', boxShadow: '0 20px 40px rgba(0,0,0,0.08)' }} />
              <Bar dataKey="value" radius={[10, 10, 0, 0]}>
                {stageData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Iris AI briefing — full width, not cramped */}
      <div className="glass p-8" data-testid="ai-briefing-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline flex items-center gap-2"><Sparkles size={12} className="text-[#679286]"/> Iris AI</div>
            <div className="font-serif text-2xl mt-1">Weekly executive briefing</div>
          </div>
          {!briefing && (
            <button onClick={genBriefing} disabled={briefingLoading} data-testid="generate-briefing-btn" className="btn-gold text-sm flex items-center gap-2">
              {briefingLoading ? <><Loader2 size={14} className="animate-spin"/> Analyzing…</> : <>Generate briefing</>}
            </button>
          )}
        </div>
        {briefing ? (
          <div className="text-base text-slate-800 leading-relaxed whitespace-pre-wrap max-w-4xl">{briefing}</div>
        ) : (
          <div className="text-sm text-slate-500 max-w-2xl">Iris AI reviews your portfolio, pending approvals, cash position and risk flags — and gives you a concise executive summary in seconds.</div>
        )}
      </div>

      {/* Recent invoices */}
      <div className="glass p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">Recent activity</div>
            <div className="font-serif text-2xl">Latest invoices</div>
          </div>
          <Link to="/app/approvals" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1">Approvals inbox <ArrowRight size={12}/></Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="overline text-left border-b border-slate-200/60">
                <th className="py-3">Invoice</th><th>Description</th><th className="text-right">Amount</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(data.recent_invoices || []).map((inv) => (
                <tr key={inv.id} onClick={() => setViewInv(inv.id)} data-testid={`dash-inv-${inv.id}`}
                    className="border-b border-slate-100/70 hover:bg-white/40 cursor-pointer">
                  <td className="py-3 font-mono text-xs text-slate-600">{inv.invoice_number}</td>
                  <td className="py-3 max-w-xs truncate">{inv.description}</td>
                  <td className="py-3 text-right font-medium">{AED(inv.amount_aed)}</td>
                  <td className="py-3"><span className={`status-pill ${
                    inv.status === 'paid' ? 'status-success'
                    : inv.status === 'rejected' ? 'status-danger'
                    : 'status-warning'
                  }`}>{inv.status?.replace(/_/g, ' ')}</span></td>
                </tr>
              ))}
              {(!data.recent_invoices || data.recent_invoices.length === 0) && (
                <tr><td colSpan={4} className="py-8 text-center text-slate-400">No invoices yet — switch to Demo workspace or add one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {viewInv && <InvoiceModal invoiceId={viewInv} onClose={() => setViewInv(null)}/>}
    </div>
  );
}
